import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Home, LogOut, Package, ShoppingBag, Plus, Trash2, Check, X, Clock,
  Phone, User, Calendar, Edit2, Eye, EyeOff, CheckCircle, XCircle, RefreshCw,
  MessageCircle, Send, Bell, Archive, Filter, MapPin, ExternalLink, Truck, Store, Printer
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useTheme } from '@/context/ThemeContext';
import ThemeToggle from '@/components/ThemeToggle';
import ImageUploader from '@/components/ImageUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function AdminOrders({ user, onLogout }) {
  const { isDark } = useTheme();
  
  const t = {
    bg: isDark ? 'bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#16213e]' : 'bg-gradient-to-br from-[#F9F7F2] via-[#FFFFFF] to-[#F5F3EE]',
    card: isDark ? 'bg-white/5 backdrop-blur-xl border-white/10' : 'bg-white shadow-lg border-gray-200',
    text: isDark ? 'text-white' : 'text-gray-900',
    textSecondary: isDark ? 'text-white/60' : 'text-gray-600',
    textMuted: isDark ? 'text-white/40' : 'text-gray-400',
    input: isDark ? 'bg-white/5 border-white/10 text-white placeholder-white/30' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400',
    header: isDark ? 'bg-black/20 backdrop-blur-xl border-white/10' : 'bg-white/80 backdrop-blur-xl border-gray-200',
    navLink: isDark ? 'text-white/60 hover:text-white' : 'text-gray-600 hover:text-gray-900',
    modal: isDark ? 'bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border-white/10' : 'bg-white border-gray-200 shadow-2xl',
  };

  const [activeTab, setActiveTab] = useState('orders');
  const [orderFilter, setOrderFilter] = useState('active'); // 'active', 'pending', 'confirmed', 'ready', 'archive'
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingOrderId, setCancellingOrderId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price_per_unit: '',
    unit: 'kg',
    category: 'vitella',
    min_quantity: 0.1,
    image_url: ''
  });
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [loadingApprovedCatalog, setLoadingApprovedCatalog] = useState(false);

  useEffect(() => {
    fetchData();
    // Aggiorna ogni 10 secondi per aggiornamenti quasi in tempo reale
    const interval = setInterval(() => {
      fetchOrders();
      setLastUpdate(new Date());
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadApprovedCatalog = async () => {
    const confirmed = window.confirm(
      'Questa operazione elimina i prodotti attuali e carica i 43 prodotti del listino definitivo. Continuare?'
    );
    if (!confirmed) return;

    setLoadingApprovedCatalog(true);
    try {
      const response = await axios.post(
        `${API}/admin/catalog/load-approved`,
        {},
        getAuthHeaders()
      );
      await fetchProducts();
      toast.success(
        `Catalogo caricato: ${response.data.product_count} prodotti in ${response.data.category_count} categorie`,
        { duration: 6000 }
      );
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante il caricamento del catalogo';
      toast.error(message, { duration: 8000 });
    } finally {
      setLoadingApprovedCatalog(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([fetchOrders(), fetchProducts()]);
    setLastUpdate(new Date());
    setLoading(false);
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`, getAuthHeaders());
      const newOrders = response.data;
      
      // Controlla se ci sono nuovi ordini pending
      const prevPendingCount = orders.filter(o => o.status === 'pending').length;
      const newPendingCount = newOrders.filter(o => o.status === 'pending').length;
      
      if (orders.length > 0 && newPendingCount > prevPendingCount) {
        const newCount = newPendingCount - prevPendingCount;
        toast.success(`🔔 ${newCount} nuovo/i ordine/i!`, { duration: 5000 });
        
        // Suona notifica audio
        try {
          const audio = new Audio('/notification.wav');
          audio.volume = 0.8;
          audio.play().catch(e => console.log('Audio play failed:', e));
        } catch (e) {
          console.log('Audio notification failed:', e);
        }
        
        // Notifica browser se supportato
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('🥩 Nuovo Ordine!', {
            body: `Hai ricevuto ${newCount} nuovo/i ordine/i`,
            icon: '/logo192.png',
            requireInteraction: true
          });
        }
      }
      
      setOrders(newOrders);
    } catch (error) {
      console.error('Errore caricamento ordini:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/admin/products`, getAuthHeaders());
      setProducts(response.data);
    } catch (error) {
      console.error('Errore caricamento prodotti:', error);
    }
  };

  const runPrintTest = async () => {
    try {
      const response = await axios.post(`${API}/admin/print-test`, {}, getAuthHeaders());
      toast.success(response.data?.agent_configured
        ? 'Prova di stampa inviata alla coda'
        : 'Prova inserita nella coda. Collega e avvia l’agente di stampa.');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossibile avviare la prova di stampa');
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      const response = await axios.put(`${API}/admin/orders/${orderId}/status?status=${status}`, {}, getAuthHeaders());
      const result = response.data || {};

      if (status === 'completed') {
        const pointsAwarded = Number(result.points_awarded || 0);
        if (pointsAwarded > 0) {
          toast.success(`Ordine completato: +${pointsAwarded} punti accreditati. Saldo cliente: ${Number(result.new_points || 0)} punti.`, { duration: 6000 });
        } else if (result.points_award_status === 'no_customer') {
          toast.info('Ordine completato. Nessun punto accreditato perché il cliente non è collegato a un account.', { duration: 6000 });
        } else if (result.points_award_status === 'error') {
          toast.error(result.points_award_message || 'Ordine completato, ma accredito punti non riuscito.', { duration: 7000 });
        } else if (result.points_award_status === 'already_awarded') {
          toast.success(`Ordine già completato: i ${pointsAwarded} punti erano già stati accreditati.`, { duration: 5000 });
        } else {
          toast.success('Ordine completato. Il totale non ha maturato punti.', { duration: 5000 });
        }
      } else {
        toast.success('Stato aggiornato');
      }
      fetchOrders();
      
      // If status is "ready", the client will receive in-app notification automatically
      if (status === 'ready') {
        toast.success('✅ Il cliente riceverà la notifica quando apre l\'app!', { duration: 4000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };
  
  // Open cancel modal
  const openCancelModal = (orderId) => {
    setCancellingOrderId(orderId);
    setCancelReason('');
    setShowCancelModal(true);
  };
  
  // Cancel order with reason
  const confirmCancelOrder = async () => {
    if (!cancelReason.trim()) {
      toast.error('Inserisci il motivo dell\'annullamento');
      return;
    }
    
    try {
      await axios.put(
        `${API}/admin/orders/${cancellingOrderId}/cancel`, 
        { reason: cancelReason },
        getAuthHeaders()
      );
      toast.success('Ordine annullato');
      setShowCancelModal(false);
      setCancellingOrderId(null);
      setCancelReason('');
      fetchOrders();
    } catch (error) {
      toast.error('Errore nell\'annullamento');
    }
  };
  
  // Function to toggle product availability
  const toggleProductAvailability = async (productId, currentAvailable) => {
    try {
      await axios.put(`${API}/admin/products/${productId}/availability`, 
        { available: !currentAvailable }, 
        getAuthHeaders()
      );
      toast.success(currentAvailable ? 'Prodotto nascosto' : 'Prodotto visibile');
      fetchProducts();
    } catch (error) {
      toast.error('Errore nel cambio disponibilità');
    }
  };
  
  // Filtered orders based on filter
  const filteredOrders = orders.filter(order => {
    if (orderFilter === 'active') {
      return ['pending', 'confirmed', 'ready'].includes(order.status);
    }
    if (orderFilter === 'archive') {
      return ['completed', 'cancelled'].includes(order.status);
    }
    return order.status === orderFilter;
  });
  
  // Count by status
  const orderCounts = {
    pending: orders.filter(o => o.status === 'pending').length,
    confirmed: orders.filter(o => o.status === 'confirmed').length,
    ready: orders.filter(o => o.status === 'ready').length,
    archive: orders.filter(o => ['completed', 'cancelled'].includes(o.status)).length,
  };

  const handleCreateProduct = async () => {
    try {
      const formData = new FormData();
      formData.append('name', productForm.name);
      formData.append('description', productForm.description);
      formData.append('price_per_unit', productForm.price_per_unit);
      formData.append('unit', productForm.unit);
      formData.append('category', productForm.category);
      formData.append('min_quantity', productForm.min_quantity);
      formData.append('image_url', productForm.image_url || '');
      
      await axios.post(`${API}/admin/products`, formData, getAuthHeaders());
      
      toast.success('Prodotto creato!');
      setShowProductModal(false);
      resetProductForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleUpdateProduct = async () => {
    try {
      await axios.put(`${API}/admin/products/${editingProduct.id}`, {
        name: productForm.name,
        description: productForm.description,
        price_per_unit: parseFloat(productForm.price_per_unit),
        unit: productForm.unit,
        category: productForm.category,
        min_quantity: parseFloat(productForm.min_quantity),
        image_url: productForm.image_url || '',
        available: true
      }, getAuthHeaders());
      
      toast.success('Prodotto aggiornato!');
      setShowProductModal(false);
      setEditingProduct(null);
      resetProductForm();
      fetchProducts();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Eliminare questo prodotto?')) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`, getAuthHeaders());
      toast.success('Prodotto eliminato');
      fetchProducts();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const resetProductForm = () => {
    setProductForm({
      name: '',
      description: '',
      price_per_unit: '',
      unit: 'kg',
      category: 'vitella',
      min_quantity: 0.1,
      image_url: ''
    });
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price_per_unit: product.price_per_unit,
      unit: product.unit,
      category: product.category,
      min_quantity: product.min_quantity,
      image_url: product.image_url || ''
    });
    setShowProductModal(true);
  };

  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30',
    confirmed: 'bg-blue-500/20 text-blue-500 border-blue-500/30',
    ready: 'bg-green-500/20 text-green-500 border-green-500/30',
    completed: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    cancelled: 'bg-red-500/20 text-red-500 border-red-500/30'
  };

  const statusLabels = {
    pending: 'In Attesa',
    confirmed: 'Confermato',
    ready: 'Pronto',
    completed: 'Completato',
    cancelled: 'Annullato'
  };

  const pendingCount = orders.filter(o => o.status === 'pending').length;

  if (loading) {
    return (
      <div className={`min-h-screen ${t.bg} flex items-center justify-center`}>
        <div className={`${t.text} text-xl flex items-center gap-3`}>
          <div className="w-8 h-8 border-4 border-[#C5A059] border-t-transparent rounded-full animate-spin"></div>
          Caricamento...
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${t.bg}`}>
      {/* Header */}
      <header className={`relative z-10 border-b ${t.header}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-6">
            <Link to="/admin" className={`flex items-center gap-2 ${t.navLink} transition-colors`}>
              <Home size={20} />
              <span className="hidden sm:inline text-sm">Dashboard</span>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#5D1822] to-[#3A0F15] flex items-center justify-center shadow-lg">
                <ShoppingBag className="w-6 h-6 text-[#C5A059]" />
              </div>
              <div>
                <h1 className={`text-2xl font-bold ${t.text}`}>Ordini & Prodotti</h1>
                <p className="text-sm text-[#C5A059]">Gestisci ordini e catalogo</p>
              </div>
            </div>
            {/* Last Update Indicator */}
            <div className="text-right">
              <p className={`text-xs ${t.textSecondary}`}>Ultimo aggiornamento</p>
              <p className={`text-sm font-medium ${t.text}`}>
                {lastUpdate.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
              </p>
              <button 
                onClick={() => { fetchOrders(); setLastUpdate(new Date()); }}
                className="text-xs text-[#C5A059] hover:underline flex items-center gap-1 ml-auto"
              >
                <RefreshCw size={12} />
                Aggiorna ora
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {pendingCount > 0 && (
              <div className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold animate-pulse">
                {pendingCount} nuovi ordini
              </div>
            )}
            <ThemeToggle />
            <button onClick={onLogout} className={`p-2 rounded-lg ${t.textSecondary}`}>
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8">
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all ${
              activeTab === 'orders'
                ? 'bg-gradient-to-r from-[#5D1822] to-[#3A0F15] text-white shadow-lg'
                : isDark ? 'bg-white/5 text-white/60' : 'bg-gray-100 text-gray-600'
            }`}
          >
            <ShoppingBag size={20} />
            Ordini
            {pendingCount > 0 && (
              <span className="bg-red-500 text-white px-2 py-0.5 rounded-full text-xs">{pendingCount}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all ${
              activeTab === 'products'
                ? 'bg-gradient-to-r from-[#C5A059] to-[#8B6914] text-white shadow-lg'
                : isDark ? 'bg-white/5 text-white/60' : 'bg-gray-100 text-gray-600'
            }`}
          >
            <Package size={20} />
            Prodotti
            <span className={`${isDark ? 'bg-white/20' : 'bg-black/10'} px-2 py-0.5 rounded-full text-xs`}>{products.length}</span>
          </button>
        </div>

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-4">
            {/* Filter Tabs */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <div className="flex items-center gap-1 mr-2">
                <Filter size={16} className={t.textMuted} />
                <span className={`text-sm ${t.textMuted}`}>Filtra:</span>
              </div>
              
              <button
                onClick={() => setOrderFilter('active')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  orderFilter === 'active'
                    ? 'bg-[#5D1822] text-white'
                    : `${isDark ? 'bg-white/10 text-white/70' : 'bg-gray-100 text-gray-600'}`
                }`}
              >
                Tutti Attivi ({orderCounts.pending + orderCounts.confirmed + orderCounts.ready})
              </button>
              
              <button
                onClick={() => setOrderFilter('pending')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  orderFilter === 'pending'
                    ? 'bg-yellow-500 text-white'
                    : `${isDark ? 'bg-white/10 text-white/70' : 'bg-gray-100 text-gray-600'}`
                }`}
              >
                🆕 Nuovi ({orderCounts.pending})
              </button>
              
              <button
                onClick={() => setOrderFilter('confirmed')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  orderFilter === 'confirmed'
                    ? 'bg-blue-500 text-white'
                    : `${isDark ? 'bg-white/10 text-white/70' : 'bg-gray-100 text-gray-600'}`
                }`}
              >
                🔧 Da Preparare ({orderCounts.confirmed})
              </button>
              
              <button
                onClick={() => setOrderFilter('ready')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  orderFilter === 'ready'
                    ? 'bg-green-500 text-white'
                    : `${isDark ? 'bg-white/10 text-white/70' : 'bg-gray-100 text-gray-600'}`
                }`}
              >
                ✅ Pronti ({orderCounts.ready})
              </button>
              
              <div className={`h-6 w-px ${isDark ? 'bg-white/10' : 'bg-gray-200'} mx-2`}></div>
              
              <button
                onClick={() => setOrderFilter('archive')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  orderFilter === 'archive'
                    ? 'bg-gray-600 text-white'
                    : `${isDark ? 'bg-white/10 text-white/70' : 'bg-gray-100 text-gray-600'}`
                }`}
              >
                <Archive size={14} />
                Archivio ({orderCounts.archive})
              </button>
              
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={runPrintTest}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#5D1822] text-white text-sm font-bold"
                >
                  <Printer size={16} />
                  Prova stampa
                </button>
                <button onClick={fetchOrders} className={`p-2 rounded-lg ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
                  <RefreshCw size={18} className={t.textSecondary} />
                </button>
              </div>
            </div>

            {filteredOrders.length === 0 ? (
              <div className={`${t.card} border rounded-2xl p-16 text-center`}>
                <ShoppingBag className={`w-16 h-16 ${t.textMuted} mx-auto mb-4`} />
                <p className={t.textSecondary}>
                  {orderFilter === 'archive' ? 'Nessun ordine in archivio' : 'Nessun ordine in questa categoria'}
                </p>
              </div>
            ) : (
              filteredOrders.map(order => (
                <div key={order.id} className={`${t.card} border rounded-2xl p-6 ${order.status === 'cancelled' ? 'opacity-60' : ''}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${statusColors[order.status]}`}>
                          {statusLabels[order.status]}
                        </span>
                        <span className={`${t.textMuted} text-sm`}>
                          {new Date(order.created_at).toLocaleString('it-IT')}
                        </span>
                      </div>
                      <h3 className={`text-xl font-bold ${t.text} flex items-center gap-2`}>
                        <User size={18} />
                        {order.customer_name}
                      </h3>
                      <p className={`${t.textSecondary} flex items-center gap-2`}>
                        <Phone size={14} />
                        {order.customer_phone}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`${t.textSecondary} text-sm flex items-center gap-1 justify-end`}>
                        {order.fulfillment_type === 'delivery' ? <Truck size={14} /> : <Store size={14} />}
                        {order.fulfillment_type === 'delivery' ? 'Consegna' : 'Ritiro'}: {new Date(order.pickup_date).toLocaleDateString('it-IT')} alle {order.pickup_time}
                      </p>
                      {order.fulfillment_type === 'delivery' && (
                        <>
                          <p className={`${t.textSecondary} text-sm mt-1 max-w-sm text-right`}>
                            <MapPin size={14} className="inline mr-1" />
                            {order.delivery_address}
                          </p>
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.delivery_address || '')}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 mt-2 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold"
                          >
                            <ExternalLink size={14} />
                            Apri su Google Maps
                          </a>
                        </>
                      )}
                      <p className="text-2xl font-bold text-[#C5A059] mt-2">€{order.total_amount?.toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className={`${isDark ? 'bg-white/5' : 'bg-gray-50'} rounded-xl p-4 mb-4`}>
                    <p className={`${t.textSecondary} text-sm mb-2`}>Prodotti ordinati:</p>
                    <div className="space-y-2">
                      {order.items?.map((item, idx) => (
                        <div key={idx} className="flex justify-between">
                          <span className={t.text}>
                            {item.product_name} - {item.quantity}{item.unit}
                            {item.notes && <span className={`${t.textMuted} text-sm ml-2`}>({item.notes})</span>}
                          </span>
                          <span className={t.text}>€{item.total_price?.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {order.fulfillment_type === 'delivery' && (
                    <div className={`${isDark ? 'bg-blue-500/10' : 'bg-blue-50'} rounded-xl p-4 mb-4 text-sm`}>
                      <div className="flex justify-between gap-4">
                        <span className={t.textSecondary}>Totale prodotti</span>
                        <strong className={t.text}>€{Number(order.products_total ?? (order.total_amount - (order.delivery_fee || 0))).toFixed(2)}</strong>
                      </div>
                      <div className="flex justify-between gap-4 mt-1">
                        <span className={t.textSecondary}>Costo consegna</span>
                        <strong className={t.text}>€{Number(order.delivery_fee || 0).toFixed(2)}</strong>
                      </div>
                      {order.delivery_notes && (
                        <p className={`${t.textSecondary} mt-3`}><strong>Indicazioni:</strong> {order.delivery_notes}</p>
                      )}
                    </div>
                  )}

                  {order.notes && (
                    <p className={`${t.textSecondary} text-sm mb-4 italic`}>Note: {order.notes}</p>
                  )}

                  {order.status === 'completed' && order.loyalty_points_awarded && (
                    <div className={`${isDark ? 'bg-green-500/10 border-green-500/20' : 'bg-green-50 border-green-200'} border rounded-xl p-3 mb-4`}>
                      <p className={`${isDark ? 'text-green-300' : 'text-green-800'} text-sm font-semibold`}>
                        ✓ {Number(order.loyalty_points_awarded_count || 0)} punti accreditati automaticamente
                        {order.loyalty_points_balance_after !== undefined && order.loyalty_points_balance_after !== null
                          ? ` · Saldo cliente: ${Number(order.loyalty_points_balance_after)} punti`
                          : ''}
                      </p>
                    </div>
                  )}

                  {order.status === 'completed' && !order.loyalty_points_awarded && order.loyalty_points_award_message && (
                    <div className={`${isDark ? 'bg-amber-500/10 border-amber-500/20' : 'bg-amber-50 border-amber-200'} border rounded-xl p-3 mb-4`}>
                      <p className={`${isDark ? 'text-amber-300' : 'text-amber-800'} text-sm font-semibold`}>
                        {order.loyalty_points_award_message}
                      </p>
                      {order.loyalty_points_award_status === 'error' && (
                        <button
                          type="button"
                          onClick={() => updateOrderStatus(order.id, 'completed')}
                          className="mt-3 px-3 py-2 rounded-lg bg-amber-600 text-white text-sm font-bold"
                        >
                          Riprova accredito punti
                        </button>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  {order.status !== 'completed' && order.status !== 'cancelled' && (
                    <div className="flex flex-wrap gap-2">
                      {order.status === 'pending' && (
                        <>
                          <button
                            onClick={() => updateOrderStatus(order.id, 'confirmed')}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium flex items-center gap-1"
                          >
                            <Check size={16} />
                            Conferma
                          </button>
                          <button
                            onClick={() => openCancelModal(order.id)}
                            className="px-4 py-2 bg-red-500/20 text-red-500 rounded-lg text-sm font-medium flex items-center gap-1"
                          >
                            <X size={16} />
                            Annulla
                          </button>
                        </>
                      )}
                      {order.status === 'confirmed' && (
                        <>
                          <button
                            onClick={() => updateOrderStatus(order.id, 'ready')}
                            className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium flex items-center gap-1"
                          >
                            <CheckCircle size={16} />
                            {order.fulfillment_type === 'delivery' ? 'Pronto per consegna' : 'Pronto per ritiro'}
                          </button>
                          <button
                            onClick={() => openCancelModal(order.id)}
                            className="px-4 py-2 bg-red-500/20 text-red-500 rounded-lg text-sm font-medium flex items-center gap-1"
                          >
                            <X size={16} />
                            Annulla
                          </button>
                        </>
                      )}
                      {order.status === 'ready' && (
                        <>
                          <button
                            onClick={() => updateOrderStatus(order.id, 'completed')}
                            className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium flex items-center gap-1"
                          >
                            <Check size={16} />
                            {order.fulfillment_type === 'delivery' ? 'Consegnato' : 'Ritirato'}
                          </button>
                          <button
                            onClick={() => openCancelModal(order.id)}
                            className="px-4 py-2 bg-red-500/20 text-red-500 rounded-lg text-sm font-medium flex items-center gap-1"
                          >
                            <X size={16} />
                            Annulla
                          </button>
                        </>
                      )}
                      {/* Contatta via telefono */}
                      <a
                        href={`tel:${order.customer_phone}`}
                        className={`px-4 py-2 ${isDark ? 'bg-white/10' : 'bg-gray-100'} ${t.text} rounded-lg text-sm font-medium flex items-center gap-1`}
                      >
                        <Phone size={14} />
                        Chiama
                      </a>
                    </div>
                  )}
                  
                  {/* Show cancel reason for cancelled orders */}
                  {order.status === 'cancelled' && order.cancel_reason && (
                    <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                      <p className="text-sm text-red-400">
                        <strong>Motivo annullamento:</strong> {order.cancel_reason}
                      </p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
              <h2 className={`text-xl font-bold ${t.text}`}>Catalogo Prodotti</h2>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={loadApprovedCatalog}
                  disabled={loadingApprovedCatalog}
                  className="px-4 py-3 bg-[#5D1822] text-white rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <RefreshCw size={18} className={loadingApprovedCatalog ? 'animate-spin' : ''} />
                  {loadingApprovedCatalog ? 'Caricamento…' : 'Carica catalogo definitivo'}
                </button>
                <button
                  onClick={() => { resetProductForm(); setEditingProduct(null); setShowProductModal(true); }}
                  className="px-4 py-3 bg-gradient-to-r from-[#C5A059] to-[#8B6914] text-white rounded-xl font-semibold flex items-center justify-center gap-2"
                >
                  <Plus size={20} />
                  Nuovo Prodotto
                </button>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map(product => (
                <div 
                  key={product.id} 
                  className={`${t.card} border rounded-2xl overflow-hidden relative ${product.available === false ? 'opacity-60' : ''}`}
                >
                  {/* Badge Non Disponibile */}
                  {product.available === false && (
                    <div className="absolute top-2 left-2 z-10 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                      NON DISPONIBILE
                    </div>
                  )}
                  
                  {product.image_url ? (
                    <img src={product.image_url} alt={product.name} className="w-full h-40 object-cover" />
                  ) : (
                    <div className="w-full h-40 bg-gradient-to-br from-[#5D1822] to-[#3A0F15] flex items-center justify-center">
                      <Package className="w-12 h-12 text-[#C5A059]/50" />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className={`text-lg font-semibold ${t.text}`}>{product.name}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-100'} ${t.textSecondary}`}>
                        {product.category}
                      </span>
                    </div>
                    <p className={`${t.textSecondary} text-sm mb-3 line-clamp-2`}>{product.description}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold text-[#C5A059]">€{product.price_per_unit?.toFixed(2)}/{product.unit}</span>
                      <div className="flex gap-2">
                        {/* Toggle Disponibilità */}
                        <button
                          onClick={() => toggleProductAvailability(product.id, product.available !== false)}
                          className={`p-2 rounded-lg transition-colors ${
                            product.available !== false 
                              ? 'bg-green-500/20 text-green-500' 
                              : 'bg-red-500/20 text-red-500'
                          }`}
                          title={product.available !== false ? 'Nascondi prodotto' : 'Mostra prodotto'}
                        >
                          {product.available !== false ? <Eye size={16} /> : <EyeOff size={16} />}
                        </button>
                        <button
                          onClick={() => openEditProduct(product)}
                          className={`p-2 rounded-lg ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}
                        >
                          <Edit2 size={16} className={t.textSecondary} />
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(product.id)}
                          className="p-2 rounded-lg bg-red-500/20 text-red-500"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Cancel Order Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${t.modal} border rounded-3xl max-w-md w-full p-8`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                <X className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h3 className={`text-xl font-bold ${t.text}`}>Annulla Ordine</h3>
                <p className={`text-sm ${t.textSecondary}`}>Inserisci il motivo dell'annullamento</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${t.text} mb-2`}>
                  Motivo annullamento *
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Es: Prodotto non disponibile, cliente ha richiesto annullamento, errore nell'ordine..."
                  className={`w-full px-4 py-3 rounded-xl border ${t.input} focus:outline-none focus:ring-2 focus:ring-red-500/50 resize-none`}
                  rows={3}
                />
              </div>
              
              {/* Quick reasons */}
              <div className="flex flex-wrap gap-2">
                {['Prodotto non disponibile', 'Richiesto dal cliente', 'Errore nell\'ordine', 'Non ritirato'].map(reason => (
                  <button
                    key={reason}
                    onClick={() => setCancelReason(reason)}
                    className={`px-3 py-1 text-xs rounded-full ${isDark ? 'bg-white/10 text-white/70 hover:bg-white/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} transition-colors`}
                  >
                    {reason}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCancelModal(false)}
                className={`flex-1 px-4 py-3 rounded-xl font-medium ${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'}`}
              >
                Indietro
              </button>
              <button
                onClick={confirmCancelOrder}
                disabled={!cancelReason.trim()}
                className={`flex-1 px-4 py-3 rounded-xl font-medium text-white ${
                  cancelReason.trim() ? 'bg-red-500 hover:bg-red-600' : 'bg-red-500/50 cursor-not-allowed'
                }`}
              >
                Conferma Annullamento
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Modal */}
      {showProductModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${t.modal} border rounded-3xl max-w-lg w-full p-8 max-h-[90vh] overflow-y-auto`}>
            <h3 className={`text-2xl font-bold ${t.text} mb-6`}>
              {editingProduct ? 'Modifica Prodotto' : 'Nuovo Prodotto'}
            </h3>
            
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Nome prodotto *"
                value={productForm.name}
                onChange={(e) => setProductForm(prev => ({ ...prev, name: e.target.value }))}
                className={`w-full px-4 py-3 ${t.input} border rounded-xl`}
              />
              
              <textarea
                placeholder="Descrizione"
                value={productForm.description}
                onChange={(e) => setProductForm(prev => ({ ...prev, description: e.target.value }))}
                className={`w-full px-4 py-3 ${t.input} border rounded-xl resize-none`}
                rows="2"
              />
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block ${t.textSecondary} text-sm mb-1`}>Prezzo *</label>
                  <input
                    type="number"
                    placeholder="0.00"
                    step="0.01"
                    value={productForm.price_per_unit}
                    onChange={(e) => setProductForm(prev => ({ ...prev, price_per_unit: e.target.value }))}
                    className={`w-full px-4 py-3 ${t.input} border rounded-xl`}
                  />
                </div>
                <div>
                  <label className={`block ${t.textSecondary} text-sm mb-1`}>Unità</label>
                  <select
                    value={productForm.unit}
                    onChange={(e) => setProductForm(prev => ({ ...prev, unit: e.target.value }))}
                    className={`w-full px-4 py-3 ${t.input} border rounded-xl`}
                  >
                    <option value="kg">al kg</option>
                    <option value="g">per 100g</option>
                    <option value="pz">al pezzo</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block ${t.textSecondary} text-sm mb-1`}>Categoria</label>
                  <select
                    value={productForm.category}
                    onChange={(e) => setProductForm(prev => ({ ...prev, category: e.target.value }))}
                    className={`w-full px-4 py-3 ${t.input} border rounded-xl`}
                  >
                    <option value="pollo">Pollo</option>
                    <option value="vitella">Vitella</option>
                    <option value="maiale">Maiale</option>
                    <option value="preparati">Preparati</option>
                    <option value="pane">Pane</option>
                    <option value="vino">Vino</option>
                    <option value="uova">Uova</option>
                    <option value="salame">Salame</option>
                  </select>
                </div>
                <div>
                  <label className={`block ${t.textSecondary} text-sm mb-1`}>Quantità Min.</label>
                  <input
                    type="number"
                    step="0.1"
                    value={productForm.min_quantity}
                    onChange={(e) => setProductForm(prev => ({ ...prev, min_quantity: e.target.value }))}
                    className={`w-full px-4 py-3 ${t.input} border rounded-xl`}
                  />
                </div>
              </div>

              <ImageUploader
                label="Immagine Prodotto"
                currentImage={productForm.image_url}
                onImageChange={(url) => setProductForm(prev => ({ ...prev, image_url: url }))}
                isDark={isDark}
              />
            </div>
            
            <div className="flex gap-3 mt-8">
              <button
                onClick={() => { setShowProductModal(false); setEditingProduct(null); resetProductForm(); }}
                className={`flex-1 px-4 py-3 ${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'} rounded-xl font-medium`}
              >
                Annulla
              </button>
              <button
                onClick={editingProduct ? handleUpdateProduct : handleCreateProduct}
                disabled={!productForm.name || !productForm.price_per_unit}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-[#C5A059] to-[#8B6914] text-white rounded-xl font-semibold disabled:opacity-50"
              >
                {editingProduct ? 'Salva Modifiche' : 'Crea Prodotto'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
